import { useMemo } from 'react';
import hljs from 'highlight.js';
import { html as diff2html } from 'diff2html';
import 'diff2html/bundles/css/diff2html.min.css';
import 'highlight.js/styles/github-dark.css';

const EXT_LANG: Record<string, string> = {
  ts: 'typescript', tsx: 'typescript', js: 'javascript', jsx: 'javascript', mjs: 'javascript', cjs: 'javascript',
  java: 'java', kt: 'kotlin', py: 'python', rb: 'ruby', go: 'go', rs: 'rust', php: 'php', cs: 'csharp',
  c: 'c', h: 'c', cpp: 'cpp', hpp: 'cpp', cc: 'cpp', swift: 'swift', scala: 'scala',
  json: 'json', yml: 'yaml', yaml: 'yaml', toml: 'ini', xml: 'xml', html: 'xml', css: 'css', scss: 'scss',
  sql: 'sql', sh: 'bash', bash: 'bash', zsh: 'bash', md: 'markdown', dockerfile: 'dockerfile', gradle: 'gradle',
};

function langFor(filename: string): string | undefined {
  const base = filename.split('/').pop() ?? filename;
  if (/^dockerfile$/i.test(base)) return 'dockerfile';
  const ext = base.includes('.') ? base.split('.').pop()!.toLowerCase() : '';
  return EXT_LANG[ext];
}

/** Visualizador de um arquivo com syntax highlight (highlight.js). */
export function CodeView({ code, filename }: { code: string; filename: string }) {
  const html = useMemo(() => {
    const lang = langFor(filename);
    try {
      return lang && hljs.getLanguage(lang)
        ? hljs.highlight(code, { language: lang }).value
        : hljs.highlightAuto(code).value;
    } catch {
      return code.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] ?? c);
    }
  }, [code, filename]);
  const lines = code.split('\n').length;
  return (
    <div className="code-view">
      <div className="code-view-gutter" aria-hidden>
        {Array.from({ length: lines }, (_, i) => (
          <div key={i}>{i + 1}</div>
        ))}
      </div>
      <pre className="hljs code-view-pre"><code dangerouslySetInnerHTML={{ __html: html }} /></pre>
    </div>
  );
}

/** Visualizador de diff unificado no estilo GitHub (diff2html). */
export function DiffView({ diff, outputFormat = 'side-by-side' }: { diff: string; outputFormat?: 'side-by-side' | 'line-by-line' }) {
  const markup = useMemo(
    () =>
      diff2html(diff, {
        drawFileList: true,
        matching: 'lines',
        outputFormat,
        renderNothingWhenEmpty: false,
      }),
    [diff, outputFormat],
  );
  if (!diff.trim()) return <div className="users-muted">Sem alterações.</div>;
  return <div className="d2h-wrap" dangerouslySetInnerHTML={{ __html: markup }} />;
}
